import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  LayoutDashboard,
  User,
  ShoppingBag,
  FileText,
  ListMusic,
  Camera,
  ClipboardList,
  ClipboardCheck,
  CalendarClock,
  CalendarCheck,
  Star,
  Bell,
  LogOut,
  ArrowLeft,
  ShieldCheck,
  FolderArchive,
  Wallet,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { meApi } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";

const BASE_ITEMS_TOP = [
  { to: "/espace-client", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/espace-client/profil", label: "Détails du compte", icon: User },
  { to: "/espace-client/commandes", label: "Mes commandes", icon: ShoppingBag },
  { to: "/espace-client/documents", label: "Mes documents", icon: FolderArchive },
  { to: "/espace-client/echeances", label: "Mes échéances", icon: Wallet },
  { to: "/espace-client/contrat", label: "Mon Devis / Contrat & Assurance", icon: FileText },
  { to: "/espace-client/playlist", label: "Ma Playlist", icon: ListMusic },
  { to: "/espace-client/photobooth", label: "Mon Photobooth", icon: Camera },
  { to: "/espace-client/validations", label: "À valider", icon: ClipboardCheck },
  { to: "/espace-client/jeux", label: "Jeux & Animations", icon: Star },
];

const BASE_ITEMS_BOTTOM = [
  { to: "/espace-client/planning", label: "Mon planning", icon: CalendarClock },
  { to: "/espace-client/rdv", label: "Mes RDV à venir", icon: CalendarCheck },
  { to: "/espace-client/notifications", label: "Mes notifications", icon: Bell },
  { to: "/espace-client/avis", label: "Laisser un avis", icon: Star },
];

export default function EspaceClientLayout() {
  const { user, logout } = useAuth();
  const [isMariage, setIsMariage] = useState(true);

  useEffect(() => {
    meApi.summary().then((s) => setIsMariage(!!s?.is_mariage)).catch(() => {});
  }, []);

  const questionnaireItem = isMariage
    ? { to: "/espace-client/questionnaire", label: "Questionnaire mariage", icon: ClipboardList, testid: "ec-questionnaire-mariage" }
    : { to: "/espace-client/questionnaire-prep", label: "Questionnaire de préparation", icon: ClipboardList, testid: "ec-questionnaire-prep" };

  const ITEMS = [...BASE_ITEMS_TOP, questionnaireItem, ...BASE_ITEMS_BOTTOM];

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-[#DCB457]/[0.05] blur-3xl" />

      {/* Top bar */}
      <header className="relative border-b border-[#DCB457]/15 bg-black/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-serif-luxe text-xl md:text-2xl gold-text" data-testid="ec-brand">My Animation</Link>
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.28em] text-white/40 border-l border-[#DCB457]/20 pl-4">Espace client</span>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="ec-admin-link" className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#DCB457] border border-[#DCB457]/40 px-3 py-1.5 hover:bg-[#DCB457]/10 transition">
                <ShieldCheck size={13} strokeWidth={1.4} /> Admin
              </Link>
            )}
            <Link to="/" className="text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-[#DCB457] inline-flex items-center gap-1.5" data-testid="ec-back-site">
              <ArrowLeft size={13} strokeWidth={1.4} /> Retour au site
            </Link>
            <button
              onClick={logout}
              data-testid="ec-logout"
              className="text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-[#DCB457] inline-flex items-center gap-1.5"
            >
              <LogOut size={13} strokeWidth={1.4} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          <div className="card-luxe p-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 border border-[#DCB457]/40 rounded-full flex items-center justify-center text-[#DCB457] font-serif-luxe text-xl">
                {((user?.prenom || user?.name || "?").charAt(0) || "?").toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="font-serif-luxe text-lg truncate" data-testid="ec-user-name">{user?.prenom ? `${user.prenom} ${user.nom || ""}`.trim() : user?.name}</p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#DCB457]/80">
                  {user?.role === "admin" ? "Administrateur" : "Client"}
                </p>
              </div>
            </div>
          </div>

          <nav className="card-luxe overflow-hidden" data-testid="ec-sidebar">
            {ITEMS.map(({ to, label, icon: Icon, end, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid || `ec-nav-${to.split("/").pop() || "dashboard"}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-4 text-sm border-b border-[#DCB457]/10 last:border-b-0 transition group ${
                    isActive
                      ? "bg-[#DCB457]/10 text-[#DCB457] border-l-2 border-l-[#DCB457]"
                      : "text-white/65 hover:text-[#DCB457] hover:bg-[#DCB457]/5"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.3} />
                <span className="flex-1 truncate">{label}</span>
              </NavLink>
            ))}
            <button
              onClick={logout}
              data-testid="ec-nav-logout"
              className="w-full flex items-center gap-3 px-5 py-4 text-sm text-white/55 hover:text-red-300 hover:bg-red-500/5 transition border-t border-[#DCB457]/10"
            >
              <LogOut size={16} strokeWidth={1.3} />
              <span className="flex-1 text-left">Se déconnecter</span>
            </button>
          </nav>
        </aside>

        {/* Content */}
        <section className="lg:col-span-9 min-w-0">
          <Outlet />
        </section>
      </div>
    </div>
  );
}
